import { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, alertsAPI, analyticsAPI, getThumbnailUrl, getImageUrl } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useAuthStore } from '../stores/authStore';
import { getDualTimeInfo, formatDate } from '../utils/formatDateTime';
import { useSocketStore } from '../stores/socketStore';
import { AlertThumbnail } from '../components/ImageModal';
import {
  UsersIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FireIcon,
  ExclamationTriangleIcon,
  MapIcon,
  NoSymbolIcon,
  TruckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import clsx from 'clsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Animated counter hook
function useAnimatedCounter(endValue, duration = 1000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (typeof endValue !== 'number') return;

    let startTime;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(startValue + (endValue - startValue) * easeOutQuart));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [endValue, duration]);

  return count;
}

const DATE_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: 'Custom', value: 'custom' },
];

function getDateRange(preset) {
  // Use local date, not UTC - important for timezone correctness
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${date}`;

  switch (preset) {
    case 'today': {
      // For today: pass the same date for both start and end
      return { startDate: todayStr, endDate: todayStr };
    }
    case '7d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      const startYear = start.getFullYear();
      const startMonth = String(start.getMonth() + 1).padStart(2, '0');
      const startDate = String(start.getDate()).padStart(2, '0');
      const startStr = `${startYear}-${startMonth}-${startDate}`;
      return { startDate: startStr, endDate: todayStr };
    }
    case '30d': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      const startYear = start.getFullYear();
      const startMonth = String(start.getMonth() + 1).padStart(2, '0');
      const startDate = String(start.getDate()).padStart(2, '0');
      const startStr = `${startYear}-${startMonth}-${startDate}`;
      return { startDate: startStr, endDate: todayStr };
    }
    default:
      return null;
  }
}

// ── Detection feature metadata (defined at module scope so it's available in JSX) ──
const FEATURE_META = {
  PPE: {
    label: 'Safety / PPE',
    description: 'Detects missing or incorrect personal protective equipment: helmets, vests, gloves, safety boots, masks.',
    bg: 'bg-orange-50', border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    iconBg: 'bg-orange-100', iconText: 'text-orange-600',
    icon: ShieldCheckIcon,
  },
  FIRE: {
    label: 'Fire / Smoke',
    description: 'Detects fire flames and smoke plumes using the same detection logic — both are treated as the same hazard category for accurate alerting.',
    bg: 'bg-red-50', border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
    iconBg: 'bg-red-100', iconText: 'text-red-600',
    icon: FireIcon,
  },
  ZONE: {
    label: 'Zone Monitoring',
    description: 'Detects unauthorized entry into restricted, hazardous, or controlled access zones.',
    bg: 'bg-purple-50', border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
    iconBg: 'bg-purple-100', iconText: 'text-purple-600',
    icon: MapIcon,
  },
  VEHICLE: {
    label: 'Vehicle Proximity',
    description: 'Detects unsafe distances between vehicles and pedestrians, preventing collisions.',
    bg: 'bg-blue-50', border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    iconBg: 'bg-blue-100', iconText: 'text-blue-600',
    icon: ExclamationTriangleIcon,
  },
  BEHAVIOR: {
    label: 'Behavior Analysis',
    description: 'Detects unsafe worker behaviors, postures, and actions that may lead to incidents.',
    bg: 'bg-teal-50', border: 'border-teal-200',
    badge: 'bg-teal-100 text-teal-700',
    iconBg: 'bg-teal-100', iconText: 'text-teal-600',
    icon: UsersIcon,
  },
  EQUIPMENT: {
    label: 'Equipment Safety',
    description: 'Detects missing safety guards, equipment misuse, and unsafe machine operation.',
    bg: 'bg-amber-50', border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    iconBg: 'bg-amber-100', iconText: 'text-amber-600',
    icon: ExclamationTriangleIcon,
  },
  HOUSEKEEPING: {
    label: 'Housekeeping',
    description: 'Detects spills, obstructions, cluttered walkways, and poor housekeeping conditions.',
    bg: 'bg-indigo-50', border: 'border-indigo-200',
    badge: 'bg-indigo-100 text-indigo-700',
    iconBg: 'bg-indigo-100', iconText: 'text-indigo-600',
    icon: ShieldCheckIcon,
  },
  ENVIRONMENTAL: {
    label: 'Environmental',
    description: 'Detects environmental hazards: gas leaks, flooding, and air quality issues.',
    bg: 'bg-green-50', border: 'border-green-200',
    badge: 'bg-green-100 text-green-700',
    iconBg: 'bg-green-100', iconText: 'text-green-600',
    icon: FireIcon,
  },
  COMPLIANCE: {
    label: 'Compliance',
    description: 'Detects permit violations, signage non-compliance, and regulatory breaches.',
    bg: 'bg-pink-50', border: 'border-pink-200',
    badge: 'bg-pink-100 text-pink-700',
    iconBg: 'bg-pink-100', iconText: 'text-pink-600',
    icon: BellAlertIcon,
  },
};

export default function DashboardPage() {
  const { selectedSite } = useSiteStore();
  // Selectors, not a whole-store destructure — these are stable action
  // references, but subscribing to the whole store would still re-render on
  // every unrelated socket-store update (e.g. every alert batch).
  const subscribeSite = useSocketStore((state) => state.subscribeSite);
  const setActiveAlerts = useSocketStore((state) => state.setActiveAlerts);
  const { refreshUser, user } = useAuthStore();
  const navigate = useNavigate();
  const orgTimezone = user?.organization?.timezone;
  const [datePreset, setDatePreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Refresh user profile on mount to keep org timezone up-to-date
  useEffect(() => { refreshUser(); }, []);

  // Live clock that ticks every 30s (syncs with data refetch)
  const [currentTime, setCurrentTime] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when orgTimezone updates from refreshUser
  const dualTime = useMemo(() => getDualTimeInfo(currentTime), [currentTime, orgTimezone]);

  const dateRange = useMemo(() => {
    if (datePreset === 'custom' && customStart) {
      return { startDate: customStart, endDate: customEnd || customStart };
    }
    return getDateRange(datePreset);
  }, [datePreset, customStart, customEnd]);

  const isToday = datePreset === 'today';

  // Subscribe to site updates
  useEffect(() => {
    if (selectedSite?.id) {
      subscribeSite(selectedSite.id);
    }
  }, [selectedSite?.id]);

  // Fetch dashboard data
  const { data: summary, isLoading, isFetching } = useQuery({
    queryKey: ['dashboard-summary', selectedSite?.id, dateRange],
    queryFn: () => dashboardAPI.getSummary(selectedSite?.id, dateRange),
    refetchInterval: isToday ? 30000 : false,
  });

  const { data: hourlyCompliance } = useQuery({
    queryKey: ['hourly-compliance', selectedSite?.id, dateRange?.startDate],
    queryFn: () => dashboardAPI.getHourlyCompliance(selectedSite?.id, dateRange?.startDate),
    refetchInterval: isToday ? 60000 : false,
  });

  const daysInRange = useMemo(() => {
    if (!dateRange) return 7;
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  }, [dateRange]);

  const { data: dailyStats } = useQuery({
    queryKey: ['daily-stats', selectedSite?.id, dateRange],
    queryFn: () => dashboardAPI.getDailyStats(selectedSite?.id, daysInRange, dateRange),
    refetchInterval: isToday ? 300000 : false,
  });

  // Alerts are pushed via WebSocket (alert:new event), no need for aggressive polling
  // Only fetch on mount/manual refresh, WebSocket handles real-time updates
  const { data: liveAlerts } = useQuery({
    queryKey: ['live-alerts', selectedSite?.id],
    queryFn: () => dashboardAPI.getActiveAlerts(selectedSite?.id),
    refetchInterval: false, // Disabled - WebSocket pushes updates
  });

  // Detection feature samples (latest image per category)
  const { data: samplesData } = useQuery({
    queryKey: ['detection-samples', selectedSite?.id],
    queryFn: () => dashboardAPI.getDetectionSamples(selectedSite?.id),
    staleTime: 300000, // 5 min — sample images don't need to refresh often
  });

  // Alert stats for detection breakdown (byCategory) — date-filtered for KPI cards
  const { data: alertStats } = useQuery({
    queryKey: ['alert-stats', selectedSite?.id, dateRange],
    queryFn: () => alertsAPI.getAlertStats(selectedSite?.id, dateRange?.startDate || 'today'),
    refetchInterval: isToday ? 60000 : false,
  });

  // All-time stats for the overall detections pie chart (no date filter)
  const { data: allTimeStats } = useQuery({
    queryKey: ['alert-stats-all', selectedSite?.id],
    queryFn: () => alertsAPI.getAlertStats(selectedSite?.id, 'all'),
    staleTime: 120000,
  });

  // Zone alert stats — filtered by current date range
  const { data: zoneStatsData } = useQuery({
    queryKey: ['zone-stats', selectedSite?.id, dateRange],
    queryFn: () => {
      const params = { siteId: selectedSite?.id };
      if (dateRange?.startDate && dateRange?.endDate) {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      } else {
        params.period = '7d';
      }
      return analyticsAPI.getZoneStats(params);
    },
    refetchInterval: isToday ? 60000 : false,
  });

  // All-time zone stats with category breakdown (for Cumulative Detections section)
  const { data: allTimeZoneData } = useQuery({
    queryKey: ['zone-stats-all-time', selectedSite?.id],
    queryFn: () => analyticsAPI.getZoneStats({ siteId: selectedSite?.id, period: 'all' }),
    staleTime: 300000,
  });

  // Sync active alerts count when liveAlerts data changes (onSuccess removed in React Query v5)
  useEffect(() => {
    if (liveAlerts?.data) {
      setActiveAlerts(Array.isArray(liveAlerts.data) ? liveAlerts.data.length : 0);
    }
  }, [liveAlerts?.data]);

  // Update active alerts count
  useEffect(() => {
    if (summary?.data?.alerts?.active !== undefined) {
      setActiveAlerts(summary.data.alerts.active);
    }
  }, [summary?.data?.alerts?.active]);

  const data = summary?.data;
  const hasData = !!data;
  const alerts = liveAlerts?.data || [];
  const statsData = alertStats?.data;
  const allTimeData = allTimeStats?.data;

  // Merge SMOKE into FIRE — fire and smoke are the same hazard category
  const rawByCategory = allTimeData?.byCategory || statsData?.byCategory || {};
  const byCategory = useMemo(() => {
    const merged = { ...rawByCategory };
    if (merged.SMOKE) {
      merged.FIRE = (merged.FIRE || 0) + merged.SMOKE;
      delete merged.SMOKE;
    }
    return merged;
  }, [rawByCategory]);

  const detectionSamples = samplesData?.data || [];
  const zoneStats = zoneStatsData?.data?.zones || [];
  const allTimeZones = allTimeZoneData?.data?.zones || [];


  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Safety Command Center</h1>
          <p className="page-description">
            {selectedSite ? selectedSite.name : 'All Sites'} • Real-time monitoring
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Picker */}
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1 glass-card-glow px-2 py-1.5 rounded-xl">
              {DATE_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDatePreset(opt.value)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                    datePreset === opt.value
                      ? 'text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100'
                  )}
                  style={datePreset === opt.value ? { background: 'var(--gradient-primary)' } : undefined}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2 animate-fade-in">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="input-select text-xs px-2 py-1.5"
                  max={new Date().toISOString().split('T')[0]}
                />
                <span className="text-slate-400 text-xs">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="input-select text-xs px-2 py-1.5"
                  min={customStart}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </div>
          {dualTime && (
            <div className="glass-card-glow px-4 py-2.5 flex items-center gap-3">
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-75"></div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-800">
                  {dualTime.siteTime} <span className="text-xs font-medium text-slate-500">{dualTime.siteLabel}</span>
                </p>
                {!dualTime.isSameZone && (
                  <p className="text-xs text-slate-400">
                    {dualTime.localTime} <span className="font-medium">{dualTime.localLabel}</span> (You)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Hero Card - PPE Safety */}
        <div className="hero-card lg:col-span-2">
          <div className="hero-card-content flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="hero-card-icon">
                  <ShieldCheckIcon className="w-7 h-7 text-white" />
                </div>
                {isToday && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    <span className="text-white/90 text-xs font-semibold">Live</span>
                  </div>
                )}
              </div>
              <h3 className="text-white/80 text-sm font-medium mb-1">PPE Violations {isToday ? 'Today' : ''}</h3>
              {hasData && (data?.safety?.ppeViolations?.total || 0) === 0 ? (
                <>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-8 h-8 rounded-full bg-emerald-400/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="hero-card-value text-emerald-300">All Clear</p>
                  </div>
                  <p className="text-white/60 text-sm mt-2">No PPE violations detected — excellent compliance</p>
                </>
              ) : (
                <>
                  <p className="hero-card-value">{data?.safety?.ppeViolations?.total || 0}</p>
                  <p className="text-white/60 text-sm mt-2">
                    {`${data?.safety?.ppeViolations?.high || 0} High, ${data?.safety?.ppeViolations?.medium || 0} Medium severity`}
                  </p>
                </>
              )}
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
                <UsersIcon className="w-4 h-4 text-white" />
                <span className="text-white font-semibold">{data?.workforce?.onSite || 0} on site</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
                <CalendarDaysIcon className="w-4 h-4 text-white" />
                <span className="text-white font-semibold">{data?.daysWithoutIncident || 0} days safe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Summary Card */}
        <div className={clsx(
          'glass-card-glow p-5 relative overflow-hidden',
          (data?.alerts?.active || 0) > 0 && 'ring-2 ring-red-200'
        )}>
          {(data?.alerts?.active || 0) > 0 && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
          )}
          <div className="flex items-start justify-between mb-4">
            <div className={clsx(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              (data?.alerts?.active || 0) > 0
                ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg'
                : 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg'
            )} style={{ boxShadow: (data?.alerts?.active || 0) > 0 ? '0 4px 14px rgba(239, 68, 68, 0.4)' : '0 4px 14px rgba(16, 185, 129, 0.4)' }}>
              <BellAlertIcon className="w-6 h-6 text-white" />
            </div>
            {(data?.alerts?.active || 0) > 0 && (
              <span className="badge badge-solid-danger">Needs Attention</span>
            )}
          </div>
          <p className="text-sm text-slate-500 font-medium">Active Alerts</p>
          {hasData && (data?.alerts?.active || 0) === 0 ? (
            <p className="text-2xl font-bold text-emerald-600 mt-1">All Clear</p>
          ) : (
            <>
              <p className="text-4xl font-bold text-slate-800 mt-1">{data?.alerts?.active || 0}</p>
              {/* This count has no date filter — an alert stays counted until it's
                  acknowledged/resolved, not just "today's" alerts — so spell out
                  the range it actually covers rather than implying it's today-only. */}
              {data?.alerts?.activeSince && (
                <p className="text-xs text-slate-400 mt-1">
                  {formatDate(data.alerts.activeSince)} – Today
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className={clsx(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-300',
        isFetching && !isLoading && 'opacity-60'
      )}>
        <MetricCard
          title="Workers On Site"
          value={data?.workforce?.onSite || 0}
          icon={UsersIcon}
          color="blue"
          delay={1}
        />
        <MetricCard
          title="PPE Violations"
          value={data?.safety?.ppeViolations?.total || 0}
          icon={ShieldCheckIcon}
          color={(data?.safety?.ppeViolations?.total || 0) === 0 ? 'green' : (data?.safety?.ppeViolations?.total || 0) <= 10 ? 'yellow' : 'red'}
          subtitle={hasData && (data?.safety?.ppeViolations?.total || 0) === 0 ? 'Fully compliant' : `${data?.safety?.ppeViolations?.high || 0} High severity`}
          delay={2}
        />
        <MetricCard
          title="Days Without Incident"
          value={data?.daysWithoutIncident || 0}
          icon={CalendarDaysIcon}
          color="purple"
          subtitle={hasData && (data?.daysWithoutIncident || 0) > 0 ? 'Keep it up!' : undefined}
          delay={3}
        />
        <MetricCard
          title="Fire/Smoke"
          value={data?.safety?.fireSmokeAlerts || 0}
          icon={FireIcon}
          color={(data?.safety?.fireSmokeAlerts || 0) === 0 ? 'green' : 'red'}
          subtitle={hasData && (data?.safety?.fireSmokeAlerts || 0) === 0 ? 'None today' : undefined}
          delay={4}
        />
        <MetricCard
          title="Vehicle"
          value={data?.safety?.vehicleCategoryAlerts || 0}
          icon={TruckIcon}
          color={(data?.safety?.vehicleCategoryAlerts || 0) === 0 ? 'green' : 'yellow'}
          subtitle={hasData && (data?.safety?.vehicleCategoryAlerts || 0) === 0 ? 'None today' : undefined}
          delay={5}
        />
        <MetricCard
          title="Harness"
          value={data?.safety?.harnessAlerts || 0}
          icon={ShieldExclamationIcon}
          color={(data?.safety?.harnessAlerts || 0) === 0 ? 'green' : 'yellow'}
          subtitle={hasData && (data?.safety?.harnessAlerts || 0) === 0 ? 'None today' : undefined}
          delay={6}
        />
      </div>

      {/* Main Grid */}
      <div className={clsx(
        'grid grid-cols-1 lg:grid-cols-3 gap-6 transition-opacity duration-300',
        isFetching && !isLoading && 'opacity-60'
      )}>
        {/* Violations Trend Chart */}
        <div className="lg:col-span-2 glass-card-glow p-6 animate-slide-up stagger-2 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          {/* Decorative gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-rose-500"></div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)' }}>
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Violations {isToday ? 'Today' : dateRange?.startDate}</h3>
                <p className="text-xs text-slate-500">Hourly safety violations trend</p>
              </div>
            </div>
            <span className="badge badge-warning">24 Hours</span>
          </div>
          <ViolationsTrendChart data={hourlyCompliance?.data} />
        </div>

        {/* Active Alerts */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-3 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          {/* Decorative gradient accent */}
          <div className={clsx(
            'absolute top-0 left-0 right-0 h-1',
            alerts.length > 0 ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-emerald-500 to-teal-500'
          )}></div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'w-10 h-10 rounded-xl flex items-center justify-center shadow-lg',
                alerts.length > 0
                  ? 'bg-gradient-to-br from-red-500 to-red-600'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
              )} style={{ boxShadow: alerts.length > 0 ? '0 4px 14px rgba(239, 68, 68, 0.3)' : '0 4px 14px rgba(16, 185, 129, 0.3)' }}>
                <BellAlertIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Active Alerts</h3>
            </div>
            <Link
              to="/alerts"
              className="text-sm font-medium transition-colors"
              style={{ color: 'var(--theme-600)' }}
            >
              View All →
            </Link>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <ShieldCheckIcon className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-600">No active alerts</p>
                <p className="text-sm text-slate-500">All systems nominal</p>
              </div>
            ) : (
              alerts.slice(0, 5).map((alert, index) => (
                <AlertCard key={alert.id} alert={alert} index={index} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts by Severity */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-4 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-amber-500"></div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)' }}>
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">{isToday ? "Today's" : 'Period'} Alerts</h3>
              <p className="text-xs text-slate-500">Distribution by severity level</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-48">
              <AlertsDonutChart data={data?.alerts?.bySeverity} />
            </div>
            <div className="space-y-3">
              <SeverityRow label="Critical" value={data?.alerts?.bySeverity?.CRITICAL || 0} color="red" />
              <SeverityRow label="High" value={data?.alerts?.bySeverity?.HIGH || 0} color="orange" />
              <SeverityRow label="Medium" value={data?.alerts?.bySeverity?.MEDIUM || 0} color="yellow" />
              <SeverityRow label="Low" value={data?.alerts?.bySeverity?.LOW || 0} color="blue" />
            </div>
          </div>
        </div>

        {/* Site Status */}
        <div className="glass-card-glow p-6 animate-slide-up stagger-5 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
          <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--gradient-primary)' }}></div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-primary)' }}>
              <UsersIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Site Status</h3>
              <p className="text-xs text-slate-500">Real-time compliance by location</p>
            </div>
          </div>
          <div className="space-y-3">
            {data?.sites?.list?.length > 0 ? (
              data.sites.list.map((site, index) => (
                <SiteStatusRow key={site.id} site={site} index={index} />
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No sites configured</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Stats Chart */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, var(--theme-500), var(--accent-500))' }}></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'var(--gradient-primary)', boxShadow: 'var(--shadow-primary)' }}>
              <CalendarDaysIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">{daysInRange}-Day Overview</h3>
              <p className="text-xs text-slate-500">Compliance and alert trends</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <span className="text-xs font-medium text-emerald-700">Compliance</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
              <span className="text-xs font-medium text-red-700">Alerts</span>
            </div>
          </div>
        </div>
        <DailyStatsChart data={dailyStats?.data} />
      </div>

      {/* Alerts by Camera Zone */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)' }}>
              <MapIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Alerts by Camera Zone</h3>
              <p className="text-xs text-slate-500">Total detections captured per monitored zone {isToday ? 'today' : 'in period'}</p>
            </div>
          </div>
          <Link to="/analytics" className="text-sm font-medium transition-colors" style={{ color: 'var(--theme-600)' }}>
            Zone Analytics →
          </Link>
        </div>
        <AlertsByZoneChart zones={zoneStats} />
      </div>

      {/* Detection Features Summary — always visible, merges live sample data when available */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(99,102,241,0.3)' }}>
              <ShieldCheckIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Detection Features Summary</h3>
              <p className="text-xs text-slate-500">What each AI feature detects — with real sample images from your site</p>
            </div>
          </div>
          <Link to="/alerts" className="text-sm font-medium transition-colors" style={{ color: 'var(--theme-600)' }}>
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {Object.keys(FEATURE_META).map(category => {
            const apiSample = detectionSamples.find(s => s.category === category);
            // For FIRE: also include SMOKE sample as fallback and merge counts
            const smokeSample = category === 'FIRE' ? detectionSamples.find(s => s.category === 'SMOKE') : null;
            const mergedSample = apiSample
              ? { ...apiSample, count: apiSample.count + (smokeSample?.count || 0) }
              : smokeSample
                ? { ...smokeSample, category: 'FIRE', count: smokeSample.count }
                : { category, count: 0, latestAlert: null };
            return (
              <DetectionFeatureCard
                key={category}
                sample={mergedSample}
                onClick={() => navigate(`/alerts?category=${category}`)}
              />
            );
          })}
        </div>
      </div>

      {/* Cumulative Detections — entire dataset, grouped by zones */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500"></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(239,68,68,0.3)' }}>
              <ArrowTrendingUpIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Cumulative Detections</h3>
              <p className="text-xs text-slate-500">All-time totals across PPE, Fire/Smoke, and Vehicle — entire dataset</p>
            </div>
          </div>
          <Link to="/alerts" className="text-sm font-medium transition-colors" style={{ color: 'var(--theme-600)' }}>
            View All Alerts →
          </Link>
        </div>

        {/* All-time totals bar chart */}
        <CumulativeDetectionsChart data={byCategory} />

        {/* Zone cumulative breakdown */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <MapIcon className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Cumulative Detections by Zone</h4>
            <span className="text-xs text-slate-400 ml-1">— all time</span>
          </div>
          <ZoneCumulativeDetails zones={allTimeZones} />
        </div>
      </div>

      {/* Detection Breakdown */}
      <div className="glass-card-glow p-6 animate-slide-up stagger-6 relative overflow-hidden" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 via-blue-500 to-cyan-500"></div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)' }}>
              <BellAlertIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Detection Breakdown</h3>
              <p className="text-xs text-slate-500">Detections by feature category {isToday ? 'today' : 'in period'}</p>
            </div>
          </div>
          <Link to="/alerts" className="text-sm font-medium transition-colors" style={{ color: 'var(--theme-600)' }}>
            View All Alerts →
          </Link>
        </div>

        {/* Top layout: 3 feature cards + zone chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: 3 category cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
            <DetectionCategoryCard
              label="Safety / PPE"
              description="Missing helmets, vests, gloves, footwear"
              count={byCategory.PPE || data?.safety?.ppeViolations?.total || 0}
              color="orange"
              icon={ShieldCheckIcon}
              sample={alerts.find(a => a.category === 'PPE')}
              onClick={() => navigate('/alerts?category=PPE')}
              compact
            />
            <DetectionCategoryCard
              label="Fire / Smoke"
              description="Fire flames and smoke plumes — treated as one hazard category"
              count={byCategory.FIRE || data?.safety?.fireAlerts || 0}
              color="red"
              icon={FireIcon}
              sample={alerts.find(a => a.category === 'FIRE' || a.category === 'SMOKE')}
              onClick={() => navigate('/alerts?category=FIRE')}
              compact
            />
            <DetectionCategoryCard
              label="False Detections"
              description="Alerts marked as false positives"
              count={data?.detectionSummary?.falsePositives || statsData?.byStatus?.FALSE_POSITIVE || 0}
              color="slate"
              icon={NoSymbolIcon}
              sample={null}
              onClick={() => navigate('/alerts?status=FALSE_POSITIVE')}
              compact
            />
          </div>

          {/* Right: Overall detections by category pie chart */}
          <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                  <BellAlertIcon className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Detections by Type</p>
                  <p className="text-xs text-slate-400">Overall alerts across all detection types</p>
                </div>
              </div>
              <Link
                to="/alerts"
                className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors"
              >
                View All →
              </Link>
            </div>

            {Object.values(byCategory).some(v => v > 0) ? (
              <div className="flex-1 flex flex-col justify-center">
                <AllDetectionsPieChart data={byCategory} />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                  <BellAlertIcon className="w-6 h-6 text-purple-400" />
                </div>
                <p className="text-sm text-slate-500">No detections {isToday ? 'today' : 'in this period'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <div className="h-8 skeleton w-64 mb-2"></div>
          <div className="h-4 skeleton w-40"></div>
        </div>
        <div className="h-12 skeleton w-40 rounded-xl"></div>
      </div>

      {/* Hero section skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-40 skeleton rounded-2xl"></div>
        <div className="h-40 skeleton rounded-2xl"></div>
      </div>

      {/* KPI cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="glass-card p-5 h-36 relative overflow-hidden">
            <div className="w-14 h-14 skeleton rounded-2xl mb-4"></div>
            <div className="h-4 skeleton w-24 mb-2"></div>
            <div className="h-8 skeleton w-16"></div>
          </div>
        ))}
      </div>

      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card p-6 h-80">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 skeleton rounded-xl"></div>
            <div>
              <div className="h-5 skeleton w-32 mb-1"></div>
              <div className="h-3 skeleton w-24"></div>
            </div>
          </div>
          <div className="h-56 skeleton rounded-xl"></div>
        </div>
        <div className="glass-card p-6 h-80">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 skeleton rounded-xl"></div>
            <div className="h-5 skeleton w-28"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 skeleton rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, suffix = '', icon: Icon, color, subtitle, trend, pulse, delay }) {
  const animatedValue = useAnimatedCounter(typeof value === 'number' ? value : parseInt(value) || 0);

  const colors = {
    blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', icon: 'text-blue-600', accent: 'from-blue-500 to-blue-600' },
    green: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', icon: 'text-emerald-600', accent: 'from-emerald-500 to-emerald-600' },
    yellow: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', icon: 'text-amber-600', accent: 'from-amber-500 to-amber-600' },
    red: { bg: 'bg-red-50', iconBg: 'bg-red-100', icon: 'text-red-600', accent: 'from-red-500 to-red-600' },
    purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', icon: 'text-purple-600', accent: 'from-purple-500 to-purple-600' },
  };

  const config = colors[color];

  return (
    <div
      className={clsx(
        'metric-card group',
        pulse && 'ring-2 ring-red-200 ring-offset-2'
      )}
      style={{ animationDelay: `${delay * 0.05}s` }}
    >
      {/* Subtle accent bar */}
      <div className={clsx('absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r', config.accent)}></div>

      <div className="flex items-start justify-between">
        <div className={clsx('metric-card-icon', config.iconBg)}>
          <Icon className={clsx('w-6 h-6', config.icon)} />
        </div>
        {trend && (
          <div className={clsx(
            'flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold',
            trend === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          )}>
            {trend === 'up' ? (
              <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
            ) : (
              <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
            )}
            {trend === 'up' ? 'Good' : 'Low'}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="metric-card-label">{title}</p>
        <p className="metric-card-value">
          {animatedValue}{suffix}
        </p>
        {subtitle && (
          <p className="text-xs text-slate-500 mt-1.5 font-medium">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function AlertCard({ alert, index }) {
  const severityConfig = {
    CRITICAL: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: FireIcon,
    },
    HIGH: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      icon: ExclamationTriangleIcon,
    },
    MEDIUM: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: ExclamationTriangleIcon,
    },
    LOW: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: BellAlertIcon,
    },
  };

  const config = severityConfig[alert.severity] || severityConfig.LOW;
  const AlertIcon = config.icon;

  const timeDiff = (date) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 0) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <Link
      to="/alerts"
      className={clsx(
        'block p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-pointer',
        config.bg,
        config.border,
        alert.severity === 'CRITICAL' && 'animate-pulse-soft'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        {(alert.thumbnailUrl || alert.snapshotUrl) ? (
          <AlertThumbnail
            imageUrl={getThumbnailUrl(alert)}
            alt={alert.type}
            size="sm"
            showPlaceholder={false}
            className="flex-shrink-0"
          />
        ) : (
          <div className={clsx('p-2 rounded-lg flex-shrink-0', config.bg)}>
            <AlertIcon className={clsx('w-4 h-4', config.text)} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={clsx('font-medium text-sm truncate', config.text)}>
            {alert.type?.replace(/_/g, ' ') || 'Unknown'}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {alert.camera?.name || 'Unknown Camera'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded', config.bg, config.text)}>
            {alert.severity}
          </span>
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {timeDiff(alert.detectedAt)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function SeverityRow({ label, value, color }) {
  const colors = {
    red: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100' },
    orange: { dot: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100' },
    yellow: { dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100' },
    blue: { dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
  };

  const config = colors[color];

  return (
    <div className={clsx(
      'flex items-center justify-between p-3 rounded-xl border transition-all hover:shadow-sm',
      config.bg,
      config.border
    )}>
      <div className="flex items-center gap-3">
        <div className={clsx('w-2.5 h-2.5 rounded-full', config.dot)}></div>
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <span className={clsx('text-xl font-bold', config.text)}>{value}</span>
    </div>
  );
}

function SiteStatusRow({ site, index }) {
  const statusColor = site.camerasOnline > 0 ? 'emerald' : 'slate';

  return (
    <div
      className="p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-semibold text-slate-800">{site.name}</p>
          <p className="text-sm text-slate-500">{site.occupancy || 0} workers on site</p>
        </div>
        <div className="text-right">
          <p className={clsx('text-2xl font-bold', `text-${statusColor}-600`)}>
            {site.camerasOnline || 0}
          </p>
          <p className="text-xs text-slate-500">cameras online</p>
          {/* Only show an Offline line when there actually are offline cameras —
              otherwise it's just noise on a fully-healthy site. */}
          {site.camerasOffline > 0 && (
            <p className="text-sm font-semibold text-red-600 mt-1">
              {site.camerasOffline} offline
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{site.daysWithoutIncident || 0} days without incident</p>
        <span className={clsx(
          'px-2 py-0.5 rounded-full text-xs font-medium',
          site.camerasOnline > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
        )}>
          {site.camerasOnline > 0 ? 'Monitoring' : 'Offline'}
        </span>
      </div>
    </div>
  );
}

function ViolationsTrendChart({ data }) {
  if (!data) return <div className="h-64 skeleton rounded-xl"></div>;

  // Get current hour to show only hours up to now
  const currentHour = new Date().getHours();

  // Calculate max violations for dynamic scale
  const maxViolations = data.length > 0 ? Math.max(...data.map(d => d.violations || 0), 5) : 5;
  const yMax = Math.ceil(maxViolations * 1.2); // Add 20% headroom

  const chartData = {
    labels: data.map(d => `${d.hour}:00`),
    datasets: [
      {
        label: 'Total Violations',
        data: data.map(d => d.violations || 0),
        borderColor: 'rgb(249, 115, 22)',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 250);
          gradient.addColorStop(0, 'rgba(249, 115, 22, 0.3)');
          gradient.addColorStop(1, 'rgba(249, 115, 22, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(249, 115, 22)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(249, 115, 22)',
        pointRadius: (context) => {
          // Show points for hours with violations
          return data[context.dataIndex]?.violations > 0 ? 4 : 0;
        },
        pointHoverRadius: 6,
        borderWidth: 2,
      },
      {
        label: 'Critical/High',
        data: data.map(d => (d.criticalCount || 0) + (d.highCount || 0)),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        callbacks: {
          afterBody: (context) => {
            const idx = context[0].dataIndex;
            const item = data[idx];
            if (item && item.violations > 0) {
              return [
                `PPE: ${item.ppeViolations || 0}`,
                `Critical: ${item.criticalCount || 0}`,
                `High: ${item.highCount || 0}`,
              ];
            }
            return [];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          maxTicksLimit: 12,
        },
      },
      y: {
        min: 0,
        max: yMax,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          stepSize: Math.max(1, Math.ceil(yMax / 5)),
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  // Check if there are any violations today
  const totalViolations = data.reduce((sum, d) => sum + (d.violations || 0), 0);

  if (totalViolations === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
          <ShieldCheckIcon className="w-8 h-8 text-emerald-600" />
        </div>
        <p className="text-lg font-semibold text-slate-700">No Violations Today</p>
        <p className="text-sm text-slate-500 mt-1">All safety standards are being met</p>
      </div>
    );
  }

  return (
    <div className="h-64">
      <Bar data={chartData} options={options} />
    </div>
  );
}

const CATEGORY_CONFIG = {
  PPE:           { label: 'Safety / PPE',    color: 'rgba(249, 115, 22, 0.85)',  border: 'rgba(249, 115, 22, 1)' },
  FIRE:          { label: 'Fire / Smoke',    color: 'rgba(239, 68, 68, 0.85)',   border: 'rgba(239, 68, 68, 1)' },
  SMOKE:         { label: 'Fire / Smoke',    color: 'rgba(239, 68, 68, 0.85)',   border: 'rgba(239, 68, 68, 1)' },
  ZONE:          { label: 'Zone Breach',     color: 'rgba(139, 92, 246, 0.85)',  border: 'rgba(139, 92, 246, 1)' },
  VEHICLE:       { label: 'Vehicle',         color: 'rgba(59, 130, 246, 0.85)',  border: 'rgba(59, 130, 246, 1)' },
  BEHAVIOR:      { label: 'Behavior',        color: 'rgba(20, 184, 166, 0.85)',  border: 'rgba(20, 184, 166, 1)' },
  EQUIPMENT:     { label: 'Equipment',       color: 'rgba(245, 158, 11, 0.85)',  border: 'rgba(245, 158, 11, 1)' },
  HOUSEKEEPING:  { label: 'Housekeeping',    color: 'rgba(99, 102, 241, 0.85)',  border: 'rgba(99, 102, 241, 1)' },
  ENVIRONMENTAL: { label: 'Environmental',   color: 'rgba(34, 197, 94, 0.85)',   border: 'rgba(34, 197, 94, 1)' },
  COMPLIANCE:    { label: 'Compliance',      color: 'rgba(236, 72, 153, 0.85)',  border: 'rgba(236, 72, 153, 1)' },
  OTHER:         { label: 'Other',           color: 'rgba(148, 163, 184, 0.85)', border: 'rgba(148, 163, 184, 1)' },
};

function ActiveAlertsByCategoryChart({ data }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  const total = entries.reduce((sum, [, v]) => sum + v, 0);

  const chartData = {
    labels: entries.map(([k]) => CATEGORY_CONFIG[k]?.label || k),
    datasets: [{
      data: entries.map(([, v]) => v),
      backgroundColor: entries.map(([k]) => CATEGORY_CONFIG[k]?.color || 'rgba(148,163,184,0.85)'),
      borderColor: ['#fff'],
      borderWidth: 2,
      hoverOffset: 6,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255,255,255,0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148,163,184,0.3)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} (${Math.round(ctx.raw / total * 100)}%)`,
        },
      },
    },
  };

  return (
    <div>
      <div className="h-36 w-full">
        <Pie data={chartData} options={options} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
        {entries.map(([key, count]) => {
          const cfg = CATEGORY_CONFIG[key] || CATEGORY_CONFIG.OTHER;
          return (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: cfg.border }}></div>
              <span className="text-xs text-slate-600">{cfg.label}:</span>
              <span className="text-xs font-bold text-slate-800">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AllDetectionsPieChart({ data }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  const total = entries.reduce((sum, [, v]) => sum + v, 0);

  const chartData = {
    labels: entries.map(([k]) => CATEGORY_CONFIG[k]?.label || k),
    datasets: [{
      data: entries.map(([, v]) => v),
      backgroundColor: entries.map(([k]) => CATEGORY_CONFIG[k]?.color || 'rgba(148,163,184,0.85)'),
      borderColor: ['#fff'],
      borderWidth: 2,
      hoverOffset: 8,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255,255,255,0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148,163,184,0.3)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw.toLocaleString()} (${Math.round(ctx.raw / total * 100)}%)`,
        },
      },
    },
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative h-48 w-48">
        <Pie data={chartData} options={options} />
      </div>
      <div className="w-full grid grid-cols-2 gap-x-4 gap-y-2">
        {entries.map(([key, count]) => {
          const cfg = CATEGORY_CONFIG[key] || CATEGORY_CONFIG.OTHER;
          const pct = Math.round(count / total * 100);
          return (
            <div key={key} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: cfg.border }}></div>
              <span className="text-xs text-slate-600 truncate">{cfg.label}</span>
              <span className="text-xs font-bold text-slate-800 ml-auto flex-shrink-0">{count.toLocaleString()} <span className="font-normal text-slate-400">({pct}%)</span></span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400">Total: <span className="font-semibold text-slate-600">{total.toLocaleString()}</span> detections</p>
    </div>
  );
}

function AlertsDonutChart({ data }) {
  if (!data) return <div className="h-full skeleton rounded-full"></div>;

  const chartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.CRITICAL || 0,
          data.HIGH || 0,
          data.MEDIUM || 0,
          data.LOW || 0,
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(59, 130, 246, 0.8)',
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(59, 130, 246, 1)',
        ],
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
      },
    },
  };

  const total = (data.CRITICAL || 0) + (data.HIGH || 0) + (data.MEDIUM || 0) + (data.LOW || 0);

  return (
    <div className="relative h-full">
      <Doughnut data={chartData} options={options} />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-3xl font-bold text-slate-800">{total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
      </div>
    </div>
  );
}

function DailyStatsChart({ data }) {
  if (!data || data.length === 0) return <div className="h-64 skeleton rounded-xl"></div>;

  const chartData = {
    labels: data.map(d => d.date),
    datasets: [
      {
        label: 'Compliance %',
        data: data.map(d => d.ppeCompliance),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 250);
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.15)');
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
          return gradient;
        },
        yAxisID: 'y',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: '#fff',
      },
      {
        label: 'Alerts',
        data: data.map(d => d.totalAlerts),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        yAxisID: 'y1',
        type: 'bar',
        borderRadius: 8,
        barThickness: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: { color: 'rgba(100, 116, 139, 0.8)' },
      },
      y: {
        type: 'linear',
        position: 'left',
        min: 0,
        max: 100,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          callback: (v) => `${v}%`
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: 0,
        grid: { drawOnChartArea: false },
        ticks: { color: 'rgba(100, 116, 139, 0.8)' },
      },
    },
  };

  return (
    <div className="h-64">
      <Line data={chartData} options={options} />
    </div>
  );
}

function DetectionCategoryCard({ label, description, count, color, icon: Icon, sample, onClick, compact = false }) {
  const colorMap = {
    orange: {
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
      icon: 'text-orange-600',
      count: 'text-orange-700',
      border: 'border-orange-200',
    },
    red: {
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      icon: 'text-red-600',
      count: 'text-red-700',
      border: 'border-red-200',
    },
    purple: {
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      icon: 'text-purple-600',
      count: 'text-purple-700',
      border: 'border-purple-200',
    },
    slate: {
      bg: 'bg-slate-50',
      iconBg: 'bg-slate-100',
      icon: 'text-slate-500',
      count: 'text-slate-600',
      border: 'border-slate-200',
    },
  };

  const c = colorMap[color] || colorMap.slate;

  if (compact) {
    // Horizontal compact layout: icon | label+description | count
    return (
      <button
        onClick={onClick}
        className={clsx(
          'text-left rounded-xl border p-4 hover:shadow-md transition-all duration-200 w-full flex items-center gap-4',
          c.bg, c.border
        )}
      >
        {/* Thumbnail or icon */}
        {sample ? (
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
            <AlertThumbnail alert={sample} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className={clsx('w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0', c.iconBg)}>
            <Icon className={clsx('w-8 h-8', c.icon, 'opacity-50')} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <div className={clsx('w-5 h-5 rounded flex items-center justify-center flex-shrink-0', c.iconBg)}>
              <Icon className={clsx('w-3 h-3', c.icon)} />
            </div>
            <p className="text-sm font-semibold text-slate-800">{label}</p>
          </div>
          <p className="text-xs text-slate-500 leading-snug">{description}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className={clsx('text-2xl font-bold', c.count)}>{count}</p>
          <p className="text-xs text-slate-400">detections</p>
        </div>
      </button>
    );
  }

  // Default card layout with thumbnail preview on top
  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left rounded-xl border p-4 hover:shadow-md transition-all duration-200 w-full group',
        c.bg, c.border
      )}
    >
      {sample ? (
        <div className="w-full h-24 rounded-lg overflow-hidden mb-3 bg-slate-200">
          <AlertThumbnail alert={sample} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={clsx('w-full h-24 rounded-lg mb-3 flex items-center justify-center', c.iconBg)}>
          <Icon className={clsx('w-10 h-10', c.icon, 'opacity-40')} />
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <div className={clsx('w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0', c.iconBg)}>
              <Icon className={clsx('w-3.5 h-3.5', c.icon)} />
            </div>
            <p className="text-sm font-semibold text-slate-800 truncate">{label}</p>
          </div>
          <p className="text-xs text-slate-500 leading-snug">{description}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className={clsx('text-2xl font-bold', c.count)}>{count}</p>
          <p className="text-xs text-slate-400">detections</p>
        </div>
      </div>
    </button>
  );
}

// Category colour config for the stacked zone chart
const CATEGORY_COLORS = {
  PPE:          { bg: 'rgba(249, 115, 22, 0.75)',  border: 'rgba(249, 115, 22, 1)',  label: 'PPE' },
  FIRE:         { bg: 'rgba(239, 68, 68, 0.75)',   border: 'rgba(239, 68, 68, 1)',   label: 'Fire/Smoke' },
  SMOKE:        { bg: 'rgba(239, 68, 68, 0.75)',   border: 'rgba(239, 68, 68, 1)',   label: 'Fire/Smoke' },
  ZONE:         { bg: 'rgba(139, 92, 246, 0.75)',  border: 'rgba(139, 92, 246, 1)',  label: 'Zone' },
  VEHICLE:      { bg: 'rgba(59, 130, 246, 0.75)',  border: 'rgba(59, 130, 246, 1)',  label: 'Vehicle' },
  BEHAVIOR:     { bg: 'rgba(16, 185, 129, 0.75)',  border: 'rgba(16, 185, 129, 1)',  label: 'Behavior' },
  EQUIPMENT:    { bg: 'rgba(245, 158, 11, 0.75)',  border: 'rgba(245, 158, 11, 1)',  label: 'Equipment' },
  HOUSEKEEPING: { bg: 'rgba(99, 102, 241, 0.75)',  border: 'rgba(99, 102, 241, 1)',  label: 'Housekeeping' },
  ENVIRONMENTAL:{ bg: 'rgba(20, 184, 166, 0.75)',  border: 'rgba(20, 184, 166, 1)',  label: 'Environmental' },
  COMPLIANCE:   { bg: 'rgba(236, 72, 153, 0.75)',  border: 'rgba(236, 72, 153, 1)',  label: 'Compliance' },
  PERMIT:       { bg: 'rgba(168, 85, 247, 0.75)',  border: 'rgba(168, 85, 247, 1)',  label: 'Permit' },
};

function ZoneDetectionsChart({ zones, onZoneClick }) {
  // Collect all categories that appear across zones (preserving a preferred order)
  const CATEGORY_ORDER = ['PPE', 'FIRE', 'ZONE', 'VEHICLE', 'BEHAVIOR', 'EQUIPMENT', 'HOUSEKEEPING', 'ENVIRONMENTAL', 'COMPLIANCE', 'PERMIT'];
  const activeCategories = CATEGORY_ORDER.filter(cat =>
    zones.some(z => (z.byCategory?.[cat] || 0) > 0)
  );
  // Fallback: show total if no category breakdown available
  const useFallback = activeCategories.length === 0;

  const labels = zones.map(z => z.zoneName.length > 12 ? z.zoneName.substring(0, 12) + '…' : z.zoneName);

  const datasets = useFallback
    ? [{
        label: 'Detections',
        data: zones.map(z => z.count),
        backgroundColor: 'rgba(139, 92, 246, 0.75)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1.5,
        borderRadius: 4,
        borderSkipped: false,
      }]
    : activeCategories.map(cat => {
        const cfg = CATEGORY_COLORS[cat] || { bg: 'rgba(100,116,139,0.7)', border: 'rgba(100,116,139,1)', label: cat };
        return {
          label: cfg.label,
          data: zones.map(z => z.byCategory?.[cat] || 0),
          backgroundColor: cfg.bg,
          borderColor: cfg.border,
          borderWidth: 1,
          borderRadius: 3,
          borderSkipped: false,
          stack: 'zone',
        };
      });

  const chartData = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const idx = elements[0].index;
        if (zones[idx]) onZoneClick(zones[idx].zoneId);
      }
    },
    plugins: {
      legend: {
        display: !useFallback && activeCategories.length > 1,
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 8,
          font: { size: 10 },
          color: 'rgba(100, 116, 139, 0.9)',
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return zones[idx]?.zoneName || items[0].label;
          },
          label: (item) => {
            const v = item.raw;
            if (v === 0) return null;
            return ` ${item.dataset.label}: ${v}`;
          },
          footer: (items) => {
            const idx = items[0].dataIndex;
            const total = zones[idx]?.count || 0;
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: 'rgba(100, 116, 139, 0.8)', font: { size: 11 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(226, 232, 240, 0.6)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          font: { size: 11 },
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="h-56 cursor-pointer">
      <Bar data={chartData} options={options} />
    </div>
  );
}


function DetectionFeatureCard({ sample, onClick }) {
  const meta = FEATURE_META[sample.category] || {
    label: sample.category,
    description: 'AI-powered detection for this category.',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    badge: 'bg-slate-100 text-slate-700',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-500',
    icon: BellAlertIcon,
  };
  const Icon = meta.icon;
  const imgUrl = sample.latestAlert
    ? (sample.latestAlert.thumbnailUrl || sample.latestAlert.snapshotUrl)
    : null;
  const fullImgUrl = getImageUrl(imgUrl);

  return (
    <button
      onClick={onClick}
      className={clsx(
        'text-left rounded-xl border overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group w-full',
        meta.bg, meta.border
      )}
    >
      {/* Photo / placeholder */}
      <div className="relative w-full h-36 overflow-hidden bg-slate-100">
        {fullImgUrl && (
          <img
            src={fullImgUrl}
            alt={`${meta.label} sample`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
        {!fullImgUrl && (
          <div className={clsx('w-full h-full flex items-center justify-center', meta.bg)}>
            <Icon className={clsx('w-14 h-14 opacity-15', meta.iconText)} />
          </div>
        )}
        {/* detection count badge */}
        <span className={clsx(
          'absolute top-2 right-2 text-xs font-bold px-2 py-0.5 rounded-full shadow',
          meta.badge
        )}>
          {sample.count.toLocaleString()}
        </span>
        {/* hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
      </div>

      {/* Text */}
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <div className={clsx('w-5 h-5 rounded flex items-center justify-center flex-shrink-0', meta.iconBg)}>
            <Icon className={clsx('w-3 h-3', meta.iconText)} />
          </div>
          <p className="text-sm font-semibold text-slate-800 leading-tight">{meta.label}</p>
        </div>
        <p className="text-xs text-slate-500 leading-snug line-clamp-2">{meta.description}</p>
        {sample.latestAlert && (
          <p className="text-xs text-slate-400 mt-1.5">
            Latest: {new Date(sample.latestAlert.detectedAt).toLocaleDateString()}
          </p>
        )}
      </div>
    </button>
  );
}

// Cumulative detections pie chart — shows all-time totals for key detection categories
function CumulativeDetectionsChart({ data }) {
  if (!data || Object.values(data).every(v => v === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center bg-slate-50 rounded-xl border border-slate-100">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <ArrowTrendingUpIcon className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-sm text-slate-500">No detection data available</p>
      </div>
    );
  }

  const KEY_CATEGORIES = ['PPE', 'FIRE', 'VEHICLE', 'ZONE', 'BEHAVIOR', 'EQUIPMENT', 'HOUSEKEEPING', 'ENVIRONMENTAL', 'COMPLIANCE'];
  const entries = KEY_CATEGORIES
    .filter(k => (data[k] || 0) > 0)
    .map(k => ({ key: k, label: CATEGORY_CONFIG[k]?.label || k, count: data[k] || 0, color: CATEGORY_CONFIG[k]?.color, border: CATEGORY_CONFIG[k]?.border }));

  if (entries.length === 0) return null;

  const total = entries.reduce((s, e) => s + e.count, 0);
  const maxIndex = entries.reduce((mi, e, i, arr) => e.count > arr[mi].count ? i : mi, 0);
  const maxEntry = entries[maxIndex];

  const chartData = {
    labels: entries.map(e => e.label),
    datasets: [{
      label: 'Total Detections',
      data: entries.map(e => e.count),
      backgroundColor: entries.map(e => e.color || 'rgba(148,163,184,0.75)'),
      borderColor: entries.map(e => e.border || 'rgba(148,163,184,1)'),
      borderWidth: 2,
      hoverOffset: 8,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          color: '#334155',
          font: { size: 12, weight: '500' },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: (chart) => {
            const ds = chart.data.datasets[0];
            return chart.data.labels.map((label, i) => ({
              text: `${label}  ${ds.data[i].toLocaleString()} (${Math.round(ds.data[i] / total * 100)}%)`,
              fillStyle: ds.backgroundColor[i],
              strokeStyle: ds.borderColor[i],
              lineWidth: 2,
              hidden: false,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        animation: false,
        backgroundColor: 'rgba(255,255,255,0.97)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148,163,184,0.3)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 12,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.toLocaleString()} detections (${Math.round(ctx.parsed / total * 100)}%)`,
        },
      },
    },
  };

  return (
    <div className="space-y-4">
      {/* Summary stat row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-orange-50 rounded-xl border border-orange-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">PPE Violations</p>
          <p className="text-xl font-bold text-orange-700">{(data.PPE || 0).toLocaleString()}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Fire / Smoke</p>
          <p className="text-xl font-bold text-red-700">{(data.FIRE || 0).toLocaleString()}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Vehicle</p>
          <p className="text-xl font-bold text-blue-700">{(data.VEHICLE || 0).toLocaleString()}</p>
        </div>
      </div>
      {/* Pie chart */}
      <div style={{ height: '220px' }}>
        <Pie data={chartData} options={options} />
      </div>
      {/* Dominant category badge */}
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-700">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse inline-block"></span>
          Highest: {maxEntry.label} ({Math.round(maxEntry.count / total * 100)}%)
        </span>
        <p className="text-xs text-slate-400">
          Total: <span className="font-semibold text-slate-600">{total.toLocaleString()}</span> all-time detections
        </p>
      </div>
    </div>
  );
}

// Zone cumulative details — shows each zone's PPE/Fire+Smoke/Vehicle breakdown
function ZoneCumulativeDetails({ zones }) {
  const [expanded, setExpanded] = useState(null);

  if (!zones || zones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center bg-slate-50 rounded-xl border border-slate-100">
        <MapIcon className="w-8 h-8 text-slate-300 mb-2" />
        <p className="text-sm text-slate-500">No zone data available</p>
        <p className="text-xs text-slate-400 mt-1">Configure camera zones to see cumulative zone breakdown</p>
      </div>
    );
  }

  const topZones = zones.filter(z => z.alertCount > 0).slice(0, 15);
  if (topZones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center bg-emerald-50 rounded-xl border border-emerald-100">
        <ShieldCheckIcon className="w-8 h-8 text-emerald-400 mb-2" />
        <p className="text-sm text-slate-600">No alerts recorded in any zone</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {topZones.map((zone) => {
        const isOpen = expanded === zone.id;
        const cats = zone.byCategory || {};
        const ppe = cats.PPE || 0;
        const fire = (cats.FIRE || 0) + (cats.SMOKE || 0);
        const vehicle = cats.VEHICLE || 0;
        const other = zone.alertCount - ppe - fire - vehicle;

        return (
          <button
            key={zone.id}
            onClick={() => setExpanded(isOpen ? null : zone.id)}
            className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 hover:border-slate-300"
          >
            {/* Zone header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{zone.name}</p>
                {zone.code && <p className="text-xs text-slate-400">{zone.code}</p>}
              </div>
              <div className="flex-shrink-0 ml-2 text-right">
                <p className="text-lg font-bold text-slate-800">{zone.alertCount.toLocaleString()}</p>
                <p className="text-xs text-slate-400">total</p>
              </div>
            </div>

            {/* Key category pills */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {ppe > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                  <ShieldCheckIcon className="w-3 h-3" /> PPE {ppe}
                </span>
              )}
              {fire > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <FireIcon className="w-3 h-3" /> Fire/Smoke {fire}
                </span>
              )}
              {vehicle > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  <ExclamationTriangleIcon className="w-3 h-3" /> Vehicle {vehicle}
                </span>
              )}
              {other > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                  +{other} other
                </span>
              )}
            </div>

            {/* Progress bar showing PPE/Fire/Vehicle proportion */}
            {zone.alertCount > 0 && (
              <div className="flex rounded-full overflow-hidden h-1.5">
                {ppe > 0 && <div className="bg-orange-400" style={{ width: `${ppe / zone.alertCount * 100}%` }} />}
                {fire > 0 && <div className="bg-red-500" style={{ width: `${fire / zone.alertCount * 100}%` }} />}
                {vehicle > 0 && <div className="bg-blue-500" style={{ width: `${vehicle / zone.alertCount * 100}%` }} />}
                {other > 0 && <div className="bg-slate-300" style={{ width: `${other / zone.alertCount * 100}%` }} />}
              </div>
            )}

            {/* Expanded detail */}
            {isOpen && Object.keys(cats).length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 animate-fade-in">
                {Object.entries(cats)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, count]) => {
                    const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.OTHER;
                    return (
                      <div key={cat} className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: cfg.border }}></div>
                          <span className="text-xs text-slate-600">{cfg.label}</span>
                        </div>
                        <span className="text-xs font-semibold text-slate-700">{count}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Zone type color palette
const ZONE_TYPE_COLORS = {
  restricted:      { bar: 'rgba(239, 68, 68, 0.75)',  border: 'rgb(239, 68, 68)',   label: 'Restricted' },
  hazard:          { bar: 'rgba(249, 115, 22, 0.75)', border: 'rgb(249, 115, 22)',  label: 'Hazard' },
  crane_zone:      { bar: 'rgba(234, 179, 8, 0.75)',  border: 'rgb(234, 179, 8)',   label: 'Crane Zone' },
  excavation:      { bar: 'rgba(168, 85, 247, 0.75)', border: 'rgb(168, 85, 247)',  label: 'Excavation' },
  chemical:        { bar: 'rgba(20, 184, 166, 0.75)', border: 'rgb(20, 184, 166)',  label: 'Chemical' },
  electrical:      { bar: 'rgba(59, 130, 246, 0.75)', border: 'rgb(59, 130, 246)',  label: 'Electrical' },
  confined_space:  { bar: 'rgba(99, 102, 241, 0.75)', border: 'rgb(99, 102, 241)', label: 'Confined Space' },
  hot_work:        { bar: 'rgba(244, 63, 94, 0.75)',  border: 'rgb(244, 63, 94)',   label: 'Hot Work' },
  safe_zone:       { bar: 'rgba(16, 185, 129, 0.75)', border: 'rgb(16, 185, 129)', label: 'Safe Zone' },
  general:         { bar: 'rgba(100, 116, 139, 0.75)',border: 'rgb(100, 116, 139)', label: 'General' },
};

function zoneColor(type) {
  return ZONE_TYPE_COLORS[type] || ZONE_TYPE_COLORS.general;
}

function AlertsByZoneChart({ zones }) {
  if (!zones || zones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
          <MapIcon className="w-7 h-7 text-emerald-400" />
        </div>
        <p className="text-sm font-medium text-slate-600">No zone data available</p>
        <p className="text-xs text-slate-400 mt-1">Configure camera zones to see alert distribution</p>
      </div>
    );
  }

  // Show top 12 zones by alert count (keeps the chart readable)
  const topZones = zones.slice(0, 12);
  const hasAlerts = topZones.some(z => z.alertCount > 0);

  // Bar chart — horizontal for readability when there are many zones
  const chartData = {
    labels: topZones.map(z => z.name),
    datasets: [
      {
        label: 'Alerts',
        data: topZones.map(z => z.alertCount),
        backgroundColor: topZones.map(z => zoneColor(z.type).bar),
        borderColor: topZones.map(z => zoneColor(z.type).border),
        borderWidth: 1.5,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        titleColor: '#1e293b',
        bodyColor: '#64748b',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 10,
        padding: 12,
        callbacks: {
          title: (ctx) => ctx[0].label,
          label: (ctx) => {
            const zone = topZones[ctx.dataIndex];
            return [
              `Alerts: ${ctx.parsed.x}`,
              `Type: ${zoneColor(zone.type).label}`,
              `Risk: ${zone.riskLevel || 'N/A'}`,
              `Violations: ${zone.violationCount || 0}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: {
          color: 'rgba(100, 116, 139, 0.8)',
          precision: 0,
          font: { size: 11 },
        },
        title: {
          display: true,
          text: 'Number of Alerts',
          color: 'rgba(100, 116, 139, 0.7)',
          font: { size: 11 },
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          color: '#334155',
          font: { size: 12, weight: '500' },
          callback: (val, idx) => {
            const name = topZones[idx]?.name || '';
            return name.length > 22 ? name.slice(0, 20) + '…' : name;
          },
        },
      },
    },
  };

  const barHeight = Math.max(300, topZones.length * 44);

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-50 rounded-xl border border-slate-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Total Zones</p>
          <p className="text-xl font-bold text-slate-800">{zones.length}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Total Alerts</p>
          <p className="text-xl font-bold text-red-700">{zones.reduce((s, z) => s + z.alertCount, 0)}</p>
        </div>
        <div className="bg-orange-50 rounded-xl border border-orange-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Highest Zone</p>
          <p className="text-sm font-bold text-orange-700 truncate">{topZones[0]?.name || '—'}</p>
          <p className="text-xs text-orange-500">{topZones[0]?.alertCount ?? 0} alerts</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-3 text-center">
          <p className="text-xs text-slate-500 mb-1">Active Alerts</p>
          <p className="text-xl font-bold text-amber-700">{zones.filter(z => z.alertCount > 0).length} zones</p>
        </div>
      </div>

      {/* Chart */}
      {hasAlerts ? (
        <div style={{ height: `${barHeight}px` }}>
          <Bar data={chartData} options={options} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-center bg-slate-50 rounded-xl border border-slate-100">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <ShieldCheckIcon className="w-6 h-6 text-emerald-500" />
          </div>
          <p className="text-sm font-medium text-slate-600">No alerts in any zone</p>
          <p className="text-xs text-slate-400 mt-1">{zones.length} zones monitored — all clear</p>
        </div>
      )}

      {/* Legend — zone types present */}
      {hasAlerts && (
        <div className="flex flex-wrap gap-2 pt-1">
          {[...new Set(topZones.map(z => z.type))].map(type => {
            const c = zoneColor(type);
            return (
              <span key={type} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-50 border border-slate-200 text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.border }}></span>
                {c.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
